import { useQuery } from "@tanstack/react-query";
import { getHouseholds } from "@/api/households";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useState } from "react";
import {
  ArrowRight,
  UserRoundCheck,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import type {
  ConvertVisitorToMemberPayload,
  Visitor,
} from "@/types/visitor";

type Props = {
  open: boolean;
  visitor: Visitor | null;

  isSubmitting: boolean;
  errorMessage?: string | null;

  onClose: () => void;

  onSubmit: (
    payload: ConvertVisitorToMemberPayload
  ) => Promise<void>;
};

export function ConvertVisitorDialog({
  open,
  visitor,
  isSubmitting,
  errorMessage,
  onClose,
  onSubmit,
}: Props) {
  const { churchId } = useAuth();
  const households = useQuery({ queryKey: ["households", churchId], queryFn: () => getHouseholds(churchId!), enabled: open && !!churchId });
  const [householdId, setHouseholdId] = useState("");
  const [enrollYouth, setEnrollYouth] = useState(false);
  const [youthStart, setYouthStart] = useState("");
  const [dateJoined, setDateJoined] =
    useState("");

  const [memberType, setMemberType] =
    useState("MEMBER");

  if (!open || !visitor) {
    return null;
  }

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();

    await onSubmit({
      enroll_youth: visitor?.attendance_classification === "YOUTH" && enrollYouth,
      youth_joined_at: visitor?.attendance_classification === "YOUTH" && youthStart ? new Date(youthStart).toISOString() : null,
      date_joined: dateJoined || null,
      member_type: memberType,
      status: "ACTIVE",
      household_id: householdId || null,
      relationship_to_household: null,
      is_head_of_household: false,
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 py-8 backdrop-blur-sm">
      <div className="w-full max-w-xl overflow-hidden rounded-3xl border border-slate-700 bg-slate-900 shadow-2xl shadow-black/50">
        <div className="relative border-b border-slate-800 px-7 py-6">
          <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-400 to-indigo-500" />

          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400">
                <UserRoundCheck size={22} />
              </div>

              <div>
                <h2 className="text-2xl font-semibold text-white">
                  Convert to Member
                </h2>

                <p className="mt-1 text-sm text-slate-400">
                  Create a member profile while preserving visitor history.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-xl p-2 text-slate-500 transition hover:bg-slate-800 hover:text-white"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {visitor.attendance_classification === "YOUTH" && <div className="space-y-3 px-7 pt-5 text-sm text-slate-300">
            <label className="block"><input required type="checkbox" checked={enrollYouth} onChange={e => setEnrollYouth(e.target.checked)} /> Enroll in Youth Ministry</label>
            <label className="block">Enrollment starts (local time) *<input required type="datetime-local" className="mt-2 w-full rounded border border-slate-700 bg-slate-950 p-3" value={youthStart} onChange={e => setYouthStart(e.target.value)} /></label>
          </div>}
          <div className="space-y-6 p-7">
            <label className="block text-sm text-slate-300">Household (optional)
              <select value={householdId} onChange={e => setHouseholdId(e.target.value)} className="mt-2 w-full rounded border border-slate-700 bg-slate-950 p-3">
                <option value="">No household</option>{households.data?.map(h => <option key={h.id} value={h.id}>{h.household_name}</option>)}
              </select>
            </label>
            <div className="rounded-2xl border border-slate-800 bg-slate-950/50 p-5">
              <p className="text-lg font-semibold text-white">
                {visitor.first_name}{" "}
                {visitor.last_name}
              </p>

              <p className="mt-2 text-sm text-slate-400">
                {visitor.visit_count ?? "Unavailable"} recorded visit
                {visitor.visit_count === 1
                  ? ""
                  : "s"}
              </p>

              <div className="mt-4 flex items-center gap-2 text-sm text-emerald-400">
                <span>Visitor profile</span>
                <ArrowRight size={15} />
                <span>Member profile</span>
              </div>
            </div>

            <label className="block">
              <span className="text-sm font-medium text-slate-300">
                Date Joined
              </span>

              <input
                type="date"
                value={dateJoined}
                onChange={(event) =>
                  setDateJoined(
                    event.target.value
                  )
                }
                className="mt-2 h-12 w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm text-white focus:border-emerald-500 focus:outline-none"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-slate-300">
                Member Type
              </span>

              <select
                value={memberType}
                onChange={(event) =>
                  setMemberType(
                    event.target.value
                  )
                }
                className="mt-2 h-12 w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-sm text-white focus:border-emerald-500 focus:outline-none"
              >
                <option value="MEMBER">
                  Member
                </option>

                <option value="REGULAR_ATTENDEE">
                  Regular Attendee
                </option>
              </select>
            </label>

            {errorMessage && (
              <div className="rounded-xl border border-rose-500/20 bg-rose-500/5 px-4 py-3 text-sm text-rose-300">
                {errorMessage}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 border-t border-slate-800 px-7 py-5">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Converting..."
                : "Convert to Member"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}